import { EffectReducer } from 'use-effect-reducer';
import { PostEditorAction, PostEditorUpdateCategoriesAction } from './PostEditorActions';
import { DisconnectedProfilesType, PostEditorState } from '../../../models/post-editor';
import { PostEditorEffect } from './PostEditorEffects';
import { ImmutabilityContext } from './effects/customImmutabilityContext';
import { ErrorMessageSeverity } from '../../../constants/form';
import { setValidationReducer } from './reducers/validation.reducer';
import { bulkImportGlobalSetReducer } from './reducers/bulkImportGlobalSet.reducer';
import { updateCustomizeProfilesReducer } from './reducers/updateCustomizeProfiles.reducer';
import {
  removeLinkReducer,
  updateLinkMetadataReducer,
  updateLinkReducer,
  updateLinkSettingsReducer
} from './reducers/link.reducer';
import { setGoogleButtonReducer } from './reducers/setGoogleButton.reducer';
import { toggleEvergreenReducer } from './reducers/toggleEvergreen.reducer';
import { changeTimeZoneReducer } from './reducers/changeTimeZone.reducer';
import { customizePerProfileReducer } from './reducers/customizePerProfile.reducer';
import {
  setEvergreenRecycleExpireCountReducer,
  setEvergreenRecycleExpireDate,
  setEvergreenRecycleTypeReducer
} from './reducers/evergreenPost.reducer';
import { setPostFeedTypeReducer, setPublishMethodReducer } from './reducers/feedAndPublishMethod.reducer';
import { firstCommentUpdateReducer, toggleFirstCommentReducer } from './reducers/firstComment.reducer';
import {
  mediaAddFromLibReducer,
  mediaAddFromUploadReducer,
  mediaFailedUploadReducer,
  mediaRemoveReducer,
  mediaReorderReducer,
  mediaSetVaryReducer,
  mediaUpdateDeferredUploadedReducer,
  mediaUpdateReducer
} from './reducers/media.reducer';
import {
  recurringInitializeReducer,
  recurringResetReducer,
  recurringSetIntervalReducer,
  recurringSetValueReducer,
  recurringUpdateDateReducer
} from './reducers/recurringPost.reducer';
import {
  scheduledAddReducer,
  scheduledRemoveReducer,
  scheduledResetReducer,
  scheduledUpdateReducer
} from './reducers/schedulePost.reducer';
import { setPostStatusReducer } from './reducers/setPostStatus.reducer';
import { setPostTimingReducer } from './reducers/setPostTiming.reducer';
import { setQueuePositionReducer } from './reducers/setQueuePosition.reducer';
import {
  textAddReducer,
  textDropDoneReducer,
  textRemoveReducer,
  textUpdateReducer,
  textReorderReducer
} from './reducers/text.reducer';
import { titleUpdateReducer } from './reducers/titleUpdate.reducer';
import { validatorStateUpdater } from '../validators/helpers/validatorStateUpdater';
import { bulkImportSetLinkMetadataReducer } from './reducers/bulkImportSetLinkMetadataReducer';
import { validatePostEditorState } from './effects/submitEditor.effect';
import { removeGroupReducer } from './reducers/removeGroup.reducer';
import { updateProfilesReducer } from './reducers/updateProfiles.reducer';
/**
 * NOTE that private actions (dispatched from the effects) are prefixed with underscore (e.g. _toggleCustomizeContentPerProfileSuccess)
 * **/

export const postEditorReducer: EffectReducer<PostEditorState, PostEditorAction, PostEditorEffect> = (
  state,
  action,
  effect
) => {
  const { update: updateImmutable } = ImmutabilityContext;
  switch (action.type) {
    /**
     * PRIVATE ACTIONS
     */
    case '_failedUpdate':
      effect({ type: 'showErrorToastEffect' });
      return state;
    /**
     * PUBLIC ACTIONS
     */
    case 'bulkImportSet': {
      return bulkImportGlobalSetReducer(state, action.payload);
    }
    case 'bulkImportSetLinkMetadata': {
      return bulkImportSetLinkMetadataReducer(state, action.payload);
    }
    case 'changeTimeZone': {
      return changeTimeZoneReducer(state, action.payload);
    }
    case 'expandPostGroup': {
      const { index, ref } = action.payload;
      effect({ type: 'expandPostGroupEffect', payload: ref });
      return updateImmutable(state, {
        postGroups: {
          [index]: {
            expanded: {
              $set: true
            }
          }
        }
      });
    }
    case 'mediaUploadExternalCallback':
      effect({ type: 'mediaOnUploadCallbackEffect', payload: action.payload });
      // we already initialized the state with the content, and we are just dispatching effect for media upload
      return state;
    case 'firstCommentUpdate':
      return firstCommentUpdateReducer(state, action.payload);
    case 'initializePostGroup': {
      return updateImmutable(state, {
        postGroups: {
          0: {
            $set: action.payload
          }
        }
      });
    }
    case 'linkRemove':
      return removeLinkReducer(state, action.payload);
    case 'linkUpdate':
      return updateLinkReducer(state, action.payload);
    case 'linkUpdateMetadata':
      return updateLinkMetadataReducer(state, action.payload);
    case 'linkUpdateSettings':
      return updateLinkSettingsReducer(state, action.payload);
    case 'mediaAddFromLib':
      return mediaAddFromLibReducer(state, action.payload);
    case 'mediaAddFromUpload':
      return mediaAddFromUploadReducer(state, action.payload);
    case 'mediaFailedUpload':
      return mediaFailedUploadReducer(state, action.payload);
    case 'mediaAddFromLink':
      effect({ type: 'mediaOnUploadCallbackEffect', payload: { ...action.payload, context: 'url' } });
      return state;
    case 'mediaPasteFromUrl':
    case 'mediaFromCanva':
      {
        const { url, ...effectPayload } = action.payload;
        effect({ type: 'mediaOnUploadCallbackEffect', payload: { ...effectPayload, urls: [url], context: 'url' } });
      }
      return state;
    case 'mediaRemove':
      return mediaRemoveReducer(state, action.payload);
    case 'mediaReorder':
      return mediaReorderReducer(state, action.payload);
    case 'mediaUpdate':
      return mediaUpdateReducer(state, action.payload);
    case 'mediaUpload':
      effect({ type: 'mediaOnUploadCallbackEffect', payload: { ...action.payload, context: 'file' } });
      return state;
    case 'mediaAddFromExternalDeferred':
      effect({ type: 'mediaWithDeferredUploadEffect', payload: action.payload });
      return state;
    case 'mediaUpdateDeferredLoaded':
      return mediaUpdateDeferredUploadedReducer(state, action.payload);
    case 'mediaUploadFromThirdParty':
      {
        const { media: mediaItems, ...effectPayload } = action.payload;
        effect({
          type: 'mediaOnUploadCallbackEffect',
          payload: { ...effectPayload, mediaItems, context: 'thirdParty' }
        });
      }
      return state;
    case 'mediaSetVary':
      return mediaSetVaryReducer(state, action.payload);
    case 'recurringInitialize':
      return recurringInitializeReducer(state, action.payload);
    case 'recurringReset':
      // TODO we might not use this one
      return recurringResetReducer(state, action.payload);
    case 'recurringSetInterval':
      return recurringSetIntervalReducer(state, action.payload);
    case 'recurringSetValue':
      return recurringSetValueReducer(state, action.payload);
    case 'recurringUpdateDate':
      return recurringUpdateDateReducer(state, action.payload);
    case 'removePostGroup':
      return removeGroupReducer(state, action.payload);
    case 'scheduledAdd':
      return scheduledAddReducer(state, action.payload);
    case 'scheduledRemove':
      return scheduledRemoveReducer(state, action.payload);
    case 'scheduledReset':
      // TODO we might not use this one
      return scheduledResetReducer(state, action.payload);
    case 'scheduledUpdate':
      return scheduledUpdateReducer(state, action.payload);
    case 'setActiveCustomizeIndex': {
      const { postGroupIndex, customizeIndex } = action.payload;
      return updateImmutable(state, {
        postGroups: {
          [postGroupIndex]: {
            activeCustomizeIndex: { $set: customizeIndex }
          }
        }
      });
    }
    case 'setDisconnectedProfileToShown': {
      return updateImmutable(state, {
        disconnectedProfiles: {
          $apply: (profiles: DisconnectedProfilesType[]) => {
            const hasProfilesToUpdate = !!profiles.find(p => p.selected && !p.shown);
            return hasProfilesToUpdate ? profiles.map(p => (p.selected ? { ...p, shown: true } : p)) : profiles;
          }
        }
      });
    }
    case 'setEvergreenExpireAfterCount':
      return setEvergreenRecycleExpireCountReducer(state, action.payload);
    case 'setEvergreenExpireAfterDate':
      return setEvergreenRecycleExpireDate(state, action.payload);
    case 'setEvergreenRecycleType': {
      return setEvergreenRecycleTypeReducer(state, action.payload);
    }
    case 'setGoogleButton': {
      return setGoogleButtonReducer(state, action.payload);
    }
    case 'setLoading': {
      return updateImmutable(state, {
        loading: { $set: action.payload }
      });
    }
    case 'setPostFeedType': {
      return setPostFeedTypeReducer(state, action.payload);
    }
    case 'setPostStatus': {
      return setPostStatusReducer(state, action.payload);
    }
    case 'setPostTiming': {
      return setPostTimingReducer(state, action.payload);
    }
    case 'setPublishMethod': {
      return setPublishMethodReducer(state, action.payload);
    }
    case 'setQueuePosition': {
      return setQueuePositionReducer(state, action.payload);
    }
    case 'setValidation': {
      return setValidationReducer(state, action.payload);
    }
    case 'submitEditor':
      effect({ type: 'submitEditorEffect', payload: action.payload });
      return state;
    case 'submitErrorEditor':
      const { isValid, postGroupErrors } = validatePostEditorState(state);
      return setValidationReducer(state, { isValid, postGroupErrors });
    case 'textAdd':
      return textAddReducer(state, action.payload);
    case 'textDropDone':
      return textDropDoneReducer(state, action.payload);
    case 'textRemove':
      return textRemoveReducer(state, action.payload);
    case 'textReorder':
      return textReorderReducer(state, action.payload);
    case 'textUpdate':
      return textUpdateReducer(state, action.payload);
    case 'titleUpdate':
      return titleUpdateReducer(state, action.payload);
    case 'toggleCustomizePerProfile':
      return customizePerProfileReducer(state, action.payload);
    case 'toggleEvergreen':
      return toggleEvergreenReducer(state, action.payload);
    case 'toggleFirstComment':
      return toggleFirstCommentReducer(state, action.payload);
    case 'updateCategories': {
      const { postGroupIndex, categoryId }: PostEditorUpdateCategoriesAction['payload'] = action.payload;
      const newState = updateImmutable(state, {
        postGroups: {
          [postGroupIndex]: {
            categoryId: {
              $set: categoryId
            }
          }
        }
      });
      return validatorStateUpdater(newState, postGroupIndex, ErrorMessageSeverity.warning, ['categoryRequired']);
    }
    case 'updateCustomizeProfiles':
      return updateCustomizeProfilesReducer(state, action.payload);
    case 'updateProfiles': {
      return updateProfilesReducer(state, action.payload);
    }
    default:
      return state;
  }
};
